import { apiClient } from '@/lib/api-client';
import { refusalText, RefreshFailed, type ActionContext } from './prima-nota-actions';
import { formatName } from './prima-nota-archive';
import {
  isProducedRegister,
  isSweepResult,
  literalPattern,
  type DeclarationOnlyItem,
  type Readiness,
  type StatementFormat,
} from './prima-nota-model';

type RefreshOutcome = { notes: string[]; problems: boolean };

/** The judgement as typed on the screen, read at the moment the register is requested. */
export type LatestJudgement = {
  notes: string;
  declarations: DeclarationOnlyItem[];
  treatments: Record<string, string>;
};

type AccountRow = { accountKey: string; format?: StatementFormat };

const NOT_TAKEN_OUTCOMES = ['already-supplied', 'ambiguous', 'unreadable', 'blocked-by-existing'];

function accountLabelOf(readiness: Readiness | null, row: AccountRow): string {
  const account =
    (readiness !== null
      ? readiness.statements.find((statement) => statement.accountKey === row.accountKey)
          ?.accountLabel
      : undefined) ?? row.accountKey;
  return row.format === undefined ? account : `${account} (${formatName(row.format)})`;
}

/** Ask Drive for statements that have arrived; a source that cannot answer stops the run. */
async function refreshDrive(
  ctx: ActionContext,
  readiness: Readiness | null,
): Promise<RefreshOutcome> {
  const { t, period } = ctx;
  const sweep = await apiClient
    .POST('/v1/finance/prima-nota/{period}/archive-sweeps', { params: { path: { period } } })
    .catch((error: unknown) => {
      throw new RefreshFailed(
        t('primaNota.refresh.driveFailed', { reason: (error as Error).message }),
      );
    });
  if (sweep.error !== undefined || !sweep.response.ok) {
    throw new RefreshFailed(
      t('primaNota.refresh.driveFailed', {
        reason: refusalText(sweep.error, sweep.response.status) ?? String(sweep.response.status),
      }),
    );
  }
  const swept: unknown = sweep.data;
  if (!isSweepResult(swept) || !swept.archiveDeclared) {
    return { notes: [], problems: false };
  }
  const unreachable = swept.accounts
    .filter((row) => row.outcome === 'unreachable')
    .map((row) => accountLabelOf(readiness, row));
  if (unreachable.length > 0) {
    throw new RefreshFailed(
      t('primaNota.refresh.driveUnreachable', { accounts: unreachable.join(', ') }),
    );
  }
  const imported = swept.accounts.filter((row) => row.outcome === 'imported').length;
  // Drive answered, but for these accounts a statement there was not taken: a different one is
  // already held, two match, one would not read, or a withdrawn copy blocks it. The register is
  // produced, but it is not "from updated files" for them, and the message must not say so.
  const notTaken = swept.accounts
    .filter((row) => NOT_TAKEN_OUTCOMES.includes(row.outcome))
    .map((row) => accountLabelOf(readiness, row));
  if (notTaken.length > 0) {
    return {
      notes: [t('primaNota.refresh.driveNotTaken', { accounts: notTaken.join(', ') })],
      problems: true,
    };
  }
  return {
    notes: [
      imported > 0
        ? t('primaNota.refresh.driveNew', { count: imported })
        : t('primaNota.refresh.driveNothing'),
    ],
    problems: false,
  };
}

/** Ask nexpense for the documents file as it stands now, when its connection is active. */
async function refreshNexpense(ctx: ActionContext, readiness: Readiness | null): Promise<string> {
  const { t, period } = ctx;
  if (readiness === null || readiness.fiscalExportConfigured !== true) {
    return t('primaNota.refresh.nexpenseNotActive');
  }
  const fetched = await apiClient
    .POST('/v1/finance/prima-nota/{period}/fiscal-export-imports', {
      params: { path: { period } },
    })
    .catch((error: unknown) => {
      throw new RefreshFailed(
        t('primaNota.refresh.nexpenseFailed', { reason: (error as Error).message }),
      );
    });
  if (fetched.error !== undefined || !fetched.response.ok) {
    throw new RefreshFailed(
      t('primaNota.refresh.nexpenseFailed', {
        reason:
          refusalText(fetched.error, fetched.response.status) ?? String(fetched.response.status),
      }),
    );
  }
  const got = fetched.data as { outcome?: string; replaced?: string[] } | undefined;
  if (got?.outcome === 'already-held') {
    return t('primaNota.refresh.nexpenseSame');
  }
  return (got?.replaced?.length ?? 0) > 0
    ? t('primaNota.refresh.nexpenseReplaced')
    : t('primaNota.refresh.nexpenseStored');
}

/**
 * Ask the month's sources again, right before generating: Drive for statements that have arrived,
 * and nexpense for the documents file as it stands now. The register is then produced from what
 * they say today, never from a file taken days ago. Unchanged sources change nothing, so the
 * same register comes out; new information is stored first.
 *
 * If a source cannot answer, nothing is generated (the Human's rule, 2026-09-27): a register
 * built on possibly stale data would look current. Until the nexpense connection is active, the
 * documents file uploaded by hand is used, and the page says the export was not asked.
 */
async function refreshSources(
  ctx: ActionContext,
  readiness: Readiness | null,
): Promise<RefreshOutcome> {
  const drive = await refreshDrive(ctx, readiness);
  const nexpense = await refreshNexpense(ctx, readiness);
  return { notes: [...drive.notes, nexpense], problems: drive.problems };
}

function judgementBody(period: string, latest: LatestJudgement) {
  return {
    reviewed_for: period,
    period_notes: latest.notes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0),
    // Both were accepted by the API from the day it shipped and neither was ever sent.
    // A register produced from this screen therefore came out with every unsettled
    // movement printed as "da classificare" and the declared-costs annex empty — which
    // is not a register anybody can file. See the comment on the produced panel.
    declaration_only_items: latest.declarations.map((item) => ({
      date: item.date,
      counterparty: item.counterparty,
      ref: item.ref,
      amount: item.amount,
      treatment: item.treatment,
    })),
    open_item_treatments: Object.fromEntries(
      Object.entries(latest.treatments)
        .filter(([, treatment]) => treatment.trim().length > 0)
        .map(([text, treatment]) => [literalPattern(text), treatment.trim()]),
    ),
  };
}

export type GenerateInput = {
  readiness: Readiness | null;
  /** Read when the register is requested, not when the button was pressed. */
  latestJudgement: { current: LatestJudgement };
};

/** Refreshes the sources, or says why not; `null` when the run must stop. */
async function refreshOrStop(
  ctx: ActionContext,
  readiness: Readiness | null,
): Promise<RefreshOutcome | null> {
  try {
    return await refreshSources(ctx, readiness);
  } catch (error) {
    if (error instanceof RefreshFailed) {
      // A source may have stored something before another failed: re-read the month, and put
      // away a register built from the files it showed.
      ctx.setProduced(null);
      ctx.reload();
      ctx.setMessage({ kind: 'error', text: error.message });
      return null;
    }
    throw error;
  }
}

async function postRegister(
  ctx: ActionContext,
  latest: LatestJudgement,
  refreshed: RefreshOutcome,
) {
  const { t, period } = ctx;
  const result = await apiClient.POST('/v1/finance/prima-nota/{period}/register', {
    params: { path: { period } },
    body: { judgement: judgementBody(period, latest) } as never,
  });
  const payload: unknown = result.data;
  if (result.error !== undefined || !result.response.ok || !isProducedRegister(payload)) {
    const spoken = refusalText(result.error, result.response.status);
    if (result.response.status === 422) {
      // "Cannot be produced" reaches an enabled button only when the screen is behind the
      // month: re-read it, and put away a register built from the files it showed.
      ctx.setProduced(null);
      ctx.reload();
    }
    throw new Error(spoken ?? String(result.response.status));
  }
  ctx.setProduced(payload);
  // The sources may have gained a file: the list on screen follows the register.
  ctx.reload();
  ctx.setMessage({
    kind: refreshed.problems ? 'error' : 'ok',
    text: [
      t(refreshed.problems ? 'primaNota.refresh.doneWithProblems' : 'primaNota.refresh.done'),
      ...refreshed.notes,
    ].join(' '),
  });
}

export async function generate(ctx: ActionContext, input: GenerateInput) {
  ctx.setBusy('generate');
  ctx.setMessage(null);
  let refreshedOk = false;
  try {
    const refreshed = await refreshOrStop(ctx, input.readiness);
    if (refreshed === null) {
      return;
    }
    refreshedOk = true;
    await postRegister(ctx, input.latestJudgement.current, refreshed);
  } catch (error) {
    if (refreshedOk) {
      // The refresh may have stored or withdrawn a file before the register failed: the list on
      // screen must follow, and a register built from the files as they were must not be offered.
      ctx.setProduced(null);
      ctx.reload();
    }
    ctx.setMessage({
      kind: 'error',
      text: ctx.t('primaNota.generate.failed', { reason: (error as Error).message }),
    });
  } finally {
    ctx.setBusy(null);
  }
}
